import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  TextField,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { useModal } from '../../contexts/ModalContext';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  ink: '#0f1a13',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const chipSx = {
  fontFamily: T.font,
  fontSize: 12,
  height: 26,
  bgcolor: T.greenLt,
  color: T.green,
  border: `1px solid ${T.border}`,
  '& .MuiChip-label': { px: 1 },
  '& .MuiChip-deleteIcon': { color: T.subtle, '&:hover': { color: T.green } },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    '&.Mui-focused': { color: T.green },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    fontFamily: T.font,
    opacity: 0.65,
  },
};

const normalizeTag = (tag) => {
  if (tag == null || tag === '') return '';
  if (typeof tag === 'object') return String(tag.value ?? tag.label ?? '').trim();
  return String(tag).trim();
};

const TagComponent = ({
  open,
  onClose,
  tags = [],
  selectedTags = [],
  onSave,
  title = 'Manage tags',
  isLoading = false,
}) => {
  const [newTags, setNewTags] = useState([]);
  const [error, setError] = useState(null);
  const { openSuccessModal } = useModal();

  const isBulkTitle = useMemo(
    () => /selected leads/i.test(title || '') || /selected lead/i.test(title || ''),
    [title]
  );

  const subtitle = isBulkTitle
    ? 'Add or remove tags to categorize these leads. The same tags apply to each selected lead.'
    : 'Add or remove tags to categorize this lead.';

  const placeholder = isBulkTitle
    ? 'Tags apply to all selected leads — type and press Enter, or pick from the list'
    : 'Type and press Enter, or pick from the list';

  useEffect(() => {
    if (open) {
      setNewTags(selectedTags || []);
      setError(null);
    }
  }, [open, selectedTags]);

  const handleSave = async () => {
    try {
      setError(null);

      const tagValues = newTags.map((tag) => {
        if (typeof tag === 'object' && tag.value) return tag.value;
        return tag;
      });

      const uniqueTagValues = tagValues.filter(
        (tag, index, self) =>
          self.findIndex((t) => String(t)?.toLowerCase() === String(tag)?.toLowerCase()) === index
      );

      const result = await onSave(uniqueTagValues);

      onClose();

      const isSingleLead = title.includes('Edit Tags for');
      const successTitle = 'Tags updated';
      const successMessage = isSingleLead
        ? 'Tags were updated for this lead.'
        : `Tags were updated for ${result?.lead_ids?.length || 'selected'} lead(s).`;

      openSuccessModal(successTitle, successMessage);
    } catch (err) {
      setError(err?.message || 'Failed to update tags');
    }
  };

  const optionStrings = (tags || []).map(normalizeTag).filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: T.greenLt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: 22, color: T.green }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: T.font,
                  fontSize: 16,
                  fontWeight: 700,
                  color: T.ink,
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: T.font,
                  fontSize: 11.5,
                  color: T.muted,
                  mt: 0.25,
                  lineHeight: 1.45,
                }}
              >
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            disabled={isLoading}
            sx={{ color: T.muted, '&:hover': { color: T.ink } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Autocomplete
          multiple
          freeSolo
          options={optionStrings}
          value={newTags}
          onChange={(event, newValue) => {
            const uniqueTags = newValue.filter((tag, index, self) => {
              const tagValue = normalizeTag(tag);
              return (
                self.findIndex((t) => normalizeTag(t).toLowerCase() === tagValue.toLowerCase()) === index
              );
            });
            setNewTags(uniqueTags);
          }}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : normalizeTag(opt))}
          filterSelectedOptions
          componentsProps={{
            paper: {
              sx: {
                fontFamily: T.font,
                borderRadius: 2,
                border: `1px solid ${T.borderL}`,
                boxShadow: '0 8px 24px rgba(15, 26, 19, 0.08)',
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tags"
              variant="outlined"
              placeholder={placeholder}
              fullWidth
              sx={fieldSx}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const label =
                typeof option === 'object' && option.label != null ? option.label : option;
              return (
                <Chip
                  {...getTagProps({ index })}
                  key={index}
                  label={label}
                  size="small"
                  sx={chipSx}
                  onDelete={() => {
                    setNewTags((prev) => prev.filter((_, i) => i !== index));
                  }}
                />
              );
            })
          }
        />

        {error && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${T.redBorder}`,
              bgcolor: T.redLt,
            }}
          >
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>{error}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            minWidth: 132,
            bgcolor: T.green,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {isLoading ? <CircularProgress size={22} thickness={4} sx={{ color: '#fff' }} /> : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagComponent;
